/**
 * TransitionControls — renders ONLY the transitions allowed from the current
 * state, as dictated by the backend state-machine metadata. Never hardcodes.
 *
 * Props:
 *   stateMachine: { transitions: { [state]: [string, ...] }, terminal: [...] }
 *   currentState: string
 *   onTransition: async (targetState, note) => void
 *   disabled?: boolean
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowRight, Lock, WifiOff } from 'lucide-react';
import { useConnectivity } from '@/hooks/useConnectivity';
import { stateLabelEs } from './StateBadge';

export const TransitionControls = ({ stateMachine, currentState, onTransition, disabled = false }) => {
  const { isOffline } = useConnectivity();
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  if (!stateMachine) return null;

  const allowed = (stateMachine.transitions?.[currentState] || []);
  const isTerminal = (stateMachine.terminal || []).includes(currentState);

  if (isTerminal || allowed.length === 0) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Lock className="w-3 h-3" />
        <span>Estado terminal — sin transiciones disponibles</span>
      </div>
    );
  }

  if (isOffline) {
    return (
      <div className="flex items-center gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5" data-testid="transition-offline-blocker">
        <WifiOff className="w-3 h-3" />
        Sin conexión — las transiciones se reactivarán al restaurar la red.
      </div>
    );
  }

  const submit = async () => {
    if (!confirmTarget) return;
    setBusy(true);
    try {
      await onTransition(confirmTarget, note || undefined);
      setConfirmTarget(null);
      setNote('');
    } finally {
      setBusy(false);
    }
  };

  if (confirmTarget) {
    return (
      <div className="space-y-2 p-3 rounded-lg border border-amber-300 bg-amber-50" data-testid="transition-confirm">
        <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
          <ArrowRight className="w-4 h-4" />
          Confirmar transición a <span className="font-mono">{confirmTarget}</span>
        </div>
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Nota operacional (opcional)"
          className="bg-white"
          data-testid="transition-note"
        />
        <div className="flex gap-2">
          <Button size="sm" className="bg-amber-600 hover:bg-amber-700" disabled={busy} onClick={submit} data-testid="transition-confirm-btn">
            {busy ? 'Aplicando…' : 'Confirmar'}
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => { setConfirmTarget(null); setNote(''); }} data-testid="transition-cancel-btn">
            Cancelar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2" data-testid="transition-actions">
      {allowed.map((target) => (
        <Button
          key={target}
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => setConfirmTarget(target)}
          data-testid={`transition-to-${target}`}
          className="text-xs"
        >
          → {stateLabelEs(target)}
        </Button>
      ))}
    </div>
  );
};

export default TransitionControls;
